import { ProjectUser } from '@monorepo/shared/apiClient/types';
import { FetchUsersResponse } from '@monorepo/shared/apiClient/users';
import { EmptyState } from '@monorepo/shared/componentsV2/EmptyState';
import { useSkipWidgetLink } from '@monorepo/shared/componentsV2/SkipWidgetLink';
import { Pagination } from '@monorepo/shared/componentsV2/Table/Pagination';
import { getNextSortDirectionForColumn } from '@monorepo/shared/componentsV2/Table/sortUtils';
import { Table } from '@monorepo/shared/componentsV2/Table/Table';
import {
  SortState,
  TableColumn,
} from '@monorepo/shared/componentsV2/Table/types';
import { useHasProjectUserUpdatePermissions } from '@monorepo/shared/hooks/permissions/useHasPermissions';
import { useCurrentUser } from '@monorepo/shared/hooks/useCurrentUser';
import { useFrontendPagination } from '@monorepo/shared/hooks/useFrontendPagination';
import {
  fillHeightAndScrollable,
  verticalLayout,
} from '@monorepo/shared/styles/layout';
import { lookFor } from '@monorepo/shared/utils/lookFor';
import { permissionRoleLabels, SortDirection } from 'mapistry-shared';
import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { isPrimarySiteAdmin, isSiteAdmin } from './sharedUtils';
import { UsersTableUserActions } from './UsersTableUserActions';

type UserTableRow = ProjectUser & { id: string };

const DEFAULT_SORT: SortState = {
  columnId: 'userName',
  direction: SortDirection.ASC,
};

function getUserRoleLabel(user: ProjectUser): string {
  if (user.isOrganizationAdmin && isPrimarySiteAdmin(user)) {
    return `${permissionRoleLabels.ORGANIZATION_ADMIN}, ${permissionRoleLabels.PROJECT_PRIMARY_ADMIN}`;
  }
  if (user.isOrganizationAdmin && isSiteAdmin(user)) {
    return `${permissionRoleLabels.ORGANIZATION_ADMIN}, ${permissionRoleLabels.PROJECT_ADMIN}`;
  }
  if (user.isOrganizationAdmin) {
    return permissionRoleLabels.ORGANIZATION_ADMIN;
  }
  if (!user.projectRoles) {
    return '';
  }
  return permissionRoleLabels[user.projectRoles] || user.projectRoles;
}

function getSortableText(user: UserTableRow, columnId: string): string {
  if (columnId === 'projectRoles') {
    return getUserRoleLabel(user);
  }
  const value = user[columnId as keyof UserTableRow];
  return typeof value === 'string' ? value : '';
}

function sortUsers(users: UserTableRow[], sortState?: SortState) {
  if (!sortState?.direction) {
    return users;
  }
  const { columnId, direction } = sortState;
  return [...users].sort((a, b) => {
    const result = getSortableText(a, String(columnId)).localeCompare(
      getSortableText(b, String(columnId)),
      undefined,
      { numeric: true },
    );
    return direction === SortDirection.ASC ? result : -result;
  });
}

interface UsersTableProps {
  organizationId: string;
  projectId: string;
  users?: FetchUsersResponse;
  filter: string | null;
}

const TableContainer = styled.div`
  ${verticalLayout}
  ${fillHeightAndScrollable}
`;

const StyledTable = styled(Table)`
  height: 100%;
  padding-bottom: 4rem;
` as typeof Table;

function getColumns({
  organizationId,
  projectId,
  currentProjectUser,
  canManageUsers,
  isCurrentUserLoading,
}: {
  organizationId: string;
  projectId: string;
  currentProjectUser: ProjectUser | undefined;
  canManageUsers: boolean;
  isCurrentUserLoading: boolean;
}): TableColumn<UserTableRow>[] {
  return [
    {
      id: 'userName',
      header: 'Name',
      contents: 'userName',
      isSortable: true,
      sortAriaHeader: 'Name',
    },
    {
      id: 'userEmail',
      header: 'Email',
      contents: 'userEmail',
      isSortable: true,
      sortAriaHeader: 'Email',
    },
    {
      id: 'projectRoles',
      header: 'Role',
      contents: (u) => getUserRoleLabel(u) || '—',
      isSortable: true,
      sortAriaHeader: 'Role',
    },
    {
      id: 'jobTitle',
      header: 'Title',
      contents: 'jobTitle',
      isSortable: true,
      sortAriaHeader: 'Title',
    },
    {
      id: 'officePhoneNumber',
      header: 'Office Phone Number',
      contents: 'officePhoneNumber',
      isSortable: true,
      sortAriaHeader: 'Office Phone Number',
    },
    {
      id: 'mobilePhoneNumber',
      header: 'Mobile Phone Number',
      contents: 'mobilePhoneNumber',
      isSortable: true,
      sortAriaHeader: 'Mobile Phone Number',
    },
    {
      id: 'actions',
      header: 'Actions',
      contents: (u) =>
        isCurrentUserLoading || !currentProjectUser ? (
          ''
        ) : (
          <UsersTableUserActions
            organizationId={organizationId}
            projectId={projectId}
            user={u}
            currentUser={currentProjectUser}
            canManageUsers={canManageUsers}
          />
        ),
      align: 'center',
      width: '8%',
    },
  ];
}

export function UsersTable(props: UsersTableProps) {
  const { organizationId, projectId, users = [], filter } = props;
  const { SkipLink, SkipLinkTarget } = useSkipWidgetLink();
  const {
    currentUser: authenticatedUser,
    isLoading: isCurrentUserLoading,
    isError: isCurrentUserError,
  } = useCurrentUser();

  const canManageUsers = useHasProjectUserUpdatePermissions(projectId);

  const currentProjectUser = useMemo(
    () => users.find((u) => u.userId === authenticatedUser?.id),
    [users, authenticatedUser?.id],
  );

  // table requires row to have an id
  const tableUsers = useMemo(
    () => users.map((user) => ({ ...user, id: user.userId })),
    [users],
  );

  const [sortState, setSortState] = useState<SortState | undefined>(
    DEFAULT_SORT,
  );
  const handleSortByColumn = useCallback(
    ({ id }: TableColumn<UserTableRow>) =>
      setSortState((prev) => {
        const direction = getNextSortDirectionForColumn(prev, id);
        return direction ? { columnId: id, direction } : undefined;
      }),
    [],
  );
  const sortedTableUsers = useMemo(
    () => sortUsers(tableUsers, sortState),
    [tableUsers, sortState],
  );

  const filterRowsBy = useCallback(
    (userRow: UserTableRow) =>
      lookFor(
        filter,
        {
          ...userRow,
          projectRoles: getUserRoleLabel(userRow),
        },
        [
          'userName',
          'userEmail',
          'projectRoles',
          'jobTitle',
          'officePhoneNumber',
          'mobilePhoneNumber',
        ],
      ),
    [filter],
  );

  const { page, setPage, filteredResults, rows, rowsPerPage } =
    useFrontendPagination({
      items: sortedTableUsers,
      filterRowsBy,
      resetPageWhen: [filter, sortState],
    });

  const columns = useMemo(
    () =>
      getColumns({
        organizationId,
        projectId,
        currentProjectUser,
        canManageUsers,
        isCurrentUserLoading,
      }),
    [
      organizationId,
      projectId,
      currentProjectUser,
      canManageUsers,
      isCurrentUserLoading,
    ],
  );

  if (!users.length) {
    return <EmptyState primaryText="There are no users for this site." />;
  }

  // If loading finished but the current user isn't among this site's users,
  // they don't belong to the project site, so don't show them the user list
  if (isCurrentUserError || (!isCurrentUserLoading && !currentProjectUser)) {
    return (
      <EmptyState primaryText="There was a problem loading the current user." />
    );
  }

  return (
    <TableContainer>
      <SkipLink linkText="Skip to pagination" />
      <StyledTable
        ariaLabel="Users List"
        columns={columns}
        rows={rows}
        isPrefilteredAndNotEmpty
        makeTableFocusable
        onSortByColumn={handleSortByColumn}
        sortState={sortState}
      />
      <SkipLinkTarget />
      <Pagination
        totalRowCount={filteredResults.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
      />
    </TableContainer>
  );
}
